import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GrantUsageBar } from './GrantUsageBar';
import { grantCurrency } from '@/lib/money';
import { claimAmountLabel, expenseCategoryLabel, isLinkableClaim } from '@/lib/bills-display';
import type { GrantRequest, ReimbursementClaimLite } from '@/types/api';

/**
 * A grant's own activity: what has been spent against it (2026-09-21, spec
 * Part 3 item 4).
 *
 * Victor asked to "view activity how much of a grant have I consumed". The
 * row above answers the number; this answers what the number is MADE OF.
 *
 * This is where the retired Expenses tab's per-receipt detail went. The tab
 * was a co-equal noun beside Grants, which is what made the page read as
 * personal expense tracking with no visible connection to the grant the
 * money came from — a receipt is not a thing a family keeps, it is how a
 * grant gets spent. Nothing was deleted: the receipts moved to the level
 * they answer a question at, and the "needs your attention" group stayed at
 * page level because it is the family's to-do rather than any one grant's
 * history.
 *
 * Link and unlink are unchanged from the card this replaces, including the
 * settled-claim rule: the server refuses to move an approved or paid claim
 * between grants (409, 2026-09-19), so the control is not drawn for one.
 */

/** Claims already approved or paid against a grant — no longer relinkable. */
const SETTLED_CLAIM_STATUSES: ReadonlySet<ReimbursementClaimLite['status']> =
  new Set<ReimbursementClaimLite['status']>(['approved', 'paying', 'paid']);

export interface GrantReceiptsProps {
  grant: GrantRequest;
  /** Every unlinked receipt the family could attach here. */
  available: ReimbursementClaimLite[];
  onLink: (claimId: string) => void;
  onUnlink: (claimId: string) => void;
  busy: boolean;
}

export function GrantReceipts({ grant, available, onLink, onUnlink, busy }: GrantReceiptsProps) {
  const { t } = useTranslation(['grants', 'common']);
  const [picking, setPicking] = useState(false);
  const currency = grantCurrency(grant);
  const isApproved = grant.status === 'approved';
  const linked = grant.linked_claims ?? [];
  // "Shell · $40" — the merchant, else the category's LABEL (#2109 R3-B22:
  // it printed the raw enum), and the dollar amount through the one
  // dollars→cents boundary (R3-B6). The `·` is the catalog's.
  const lineFor = (c: ReimbursementClaimLite) => t('receipts.line', {
    label: c.vendor || expenseCategoryLabel(c.category, t),
    amount: claimAmountLabel(c.amount, currency),
  });

  return (
    <div className="border-t bg-muted/20 px-4 py-3 space-y-3" data-testid="grant-receipts">
      {/* The bar lives here rather than on the row: the row prints the one
          number a scanning reader wants, and the applied / pending
          breakdown is detail you opened the grant for.

          `showBudgetSummary={false}` is what makes that division true rather
          than merely intended. The row above is still on screen when this
          renders, and it already prints `grantRemaining()` — so until #2097
          the open card read "$11,400 left of $12,000" twice word for word,
          and named $12,000 a third time in the bar's caption. The bar now
          contributes only what the row cannot: what the budget is made of. */}
      {grant.usage && <GrantUsageBar usage={grant.usage} currency={currency} showBudgetSummary={false} />}

      <div>
        <h4 className="text-label font-medium text-foreground">{t('receipts.heading')}</h4>
        {linked.length === 0 ? (
          <p className="mt-1.5 text-body-sm text-stevens-ink-3">
            {t('receipts.none')}
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1.5">
            {linked.map((c) => (
              <li
                key={c._id}
                className="flex items-center justify-between gap-2 rounded-md bg-background px-2.5 py-1.5 text-body-sm"
              >
                <span className="truncate">
                  {lineFor(c)}
                </span>
                {isApproved && !SETTLED_CLAIM_STATUSES.has(c.status) && (
                  <button
                    type="button"
                    className="text-stevens-ink-3 hover:text-status-danger-fg disabled:opacity-50"
                    disabled={busy}
                    onClick={() => onUnlink(c._id)}
                    aria-label={t('receipts.unlink')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isApproved && (picking ? (
        <div className="space-y-1.5">
          {available.length === 0 ? (
            <p className="text-body-sm italic text-stevens-ink-3">
              {t('receipts.noneAvailable')}
            </p>
          ) : (
            available.map((c) => (
              <button
                key={c._id}
                type="button"
                disabled={busy}
                onClick={() => { onLink(c._id); setPicking(false); }}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-body-sm hover:border-stevens-indigo/30 disabled:opacity-50"
              >
                <span className="truncate">
                  {lineFor(c)}
                </span>
                <span className="text-stevens-indigo">{t('receipts.link')}</span>
              </button>
            ))
          )}
          <Button variant="ghost" size="sm" onClick={() => setPicking(false)}>{t('common:actions.cancel')}</Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
          <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> {t('receipts.attach')}
        </Button>
      ))}
    </div>
  );
}

export { isLinkableClaim };
